package fyi.moko.hotpl.ui.detail

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fyi.moko.hotpl.data.image.ImageStoreFullException
import fyi.moko.hotpl.domain.model.Emotion
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceRichness
import fyi.moko.hotpl.domain.model.Visibility
import fyi.moko.hotpl.domain.model.Visit
import fyi.moko.hotpl.domain.model.PhotoGroup
import fyi.moko.hotpl.domain.model.PhotoKind
import fyi.moko.hotpl.domain.photo.PhotoClassifier
import fyi.moko.hotpl.domain.service.EmotionLabelPreference
import fyi.moko.hotpl.domain.service.friendsWhoAlsoWantToGo
import fyi.moko.hotpl.domain.service.UsageEvents
import fyi.moko.hotpl.domain.service.UsageTracker
import fyi.moko.hotpl.domain.usecase.AddVisitUseCase
import fyi.moko.hotpl.domain.usecase.AddVisitWithPhotosUseCase
import fyi.moko.hotpl.domain.usecase.VisitPhotoDraft
import fyi.moko.hotpl.domain.usecase.DeletePlaceUseCase
import fyi.moko.hotpl.domain.usecase.ObservePlaceUseCase
import fyi.moko.hotpl.domain.usecase.ObserveFriendWishes
import fyi.moko.hotpl.domain.usecase.ObserveVisitsUseCase
import fyi.moko.hotpl.ui.placeform.PickedImage
import fyi.moko.hotpl.domain.usecase.SetPlaceVisibilityUseCase
import fyi.moko.hotpl.domain.usecase.ToggleLikedUseCase
import fyi.moko.hotpl.domain.usecase.calculatePlaceRichness
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlin.time.Clock

data class DetailUiState(
    val place: Place? = null,
    val visits: List<Visit> = emptyList(),
    val loading: Boolean = true,
) {
    val visitCount: Int get() = visits.size
    val lastVisitedAt: Long? get() = visits.firstOrNull()?.visitedAt

    /** Drives how much the detail screen "grows" as the user fills the place in. */
    val richness: PlaceRichness
        get() = place?.let { calculatePlaceRichness(it, visits) } ?: PlaceRichness()
}

/**
 * 저장 전 상태의 방문 사진. [destination]이 화면이 보여 주는 값이며, 분류기가 못 정했으면
 * 방문 쪽으로 떨어진다 — 저장 시점의 규칙([AddVisitWithPhotosUseCase])과 같아야 화면이 보여준
 * 목적지와 실제로 저장되는 자리가 어긋나지 않는다.
 */
data class PendingVisitPhoto(
    val bytes: ByteArray,
    val kind: PhotoKind? = null,
    val group: PhotoGroup? = null,
    /** 사용자가 직접 옮겼는지. 화면에서 "자동" 표시를 지우는 데 쓴다. */
    val movedByUser: Boolean = false,
) {
    val destination: PhotoGroup
        get() = when (kind?.group ?: group) {
            PhotoGroup.PLACE -> PhotoGroup.PLACE
            else -> PhotoGroup.VISIT
        }

    /** 분류기가 아무것도 못 정했다 — 화면이 "확인해 주세요"로 표시할 근거. */
    val undecided: Boolean get() = kind == null && group == null && !movedByUser

    override fun equals(other: Any?): Boolean =
        this === other || (
            other is PendingVisitPhoto &&
                bytes.contentEquals(other.bytes) && kind == other.kind &&
                group == other.group && movedByUser == other.movedByUser
            )

    override fun hashCode(): Int {
        var result = bytes.contentHashCode()
        result = 31 * result + (kind?.hashCode() ?: 0)
        result = 31 * result + (group?.hashCode() ?: 0)
        result = 31 * result + movedByUser.hashCode()
        return result
    }
}


@OptIn(ExperimentalCoroutinesApi::class)
class DetailViewModel(
    private val observePlace: ObservePlaceUseCase,
    private val observeVisits: ObserveVisitsUseCase,
    private val addVisit: AddVisitUseCase,
    private val addVisitWithPhotos: AddVisitWithPhotosUseCase,
    private val photoClassifier: PhotoClassifier,
    private val deletePlace: DeletePlaceUseCase,
    private val toggleLikedUseCase: ToggleLikedUseCase,
    private val setPlaceVisibilityUseCase: SetPlaceVisibilityUseCase,
    emotionLabelPreference: EmotionLabelPreference,
    private val usageTracker: UsageTracker,
    observeFriendWishes: ObserveFriendWishes,
) : ViewModel() {

    /** User-renamed emotion slots for the Log-a-Visit chips and the timeline ([EmotionLabelPreference]). */
    val emotionLabels: StateFlow<Map<String, String>> = emotionLabelPreference.labels

    var showLogVisitDialog by mutableStateOf(false)
        private set
    var visitNoteInput by mutableStateOf("")
        private set
    var visitEmotionInput by mutableStateOf<Emotion?>(null)
        private set
    /**
     * "Would you come back?" — 이번 방문을 기록하면서 [Place.liked]를 정한다. 다시 가고 싶다는
     * 뜻은 가 본 직후에 가장 또렷해서, 폼의 스위치를 걷어내고 여기로 옮겼다(2026-09-17). 열 때
     * 현재 값으로 시작하고, 저장 시 달라졌으면 토글한다.
     */
    var visitRevisitInput by mutableStateOf(false)
        private set

    /**
     * 이번 방문에 붙일, 아직 저장되지 않은 사진들. 분류는 사진을 고른 직후에 돌려서 사용자가
     * **저장 전에** 목적지를 보고 고칠 수 있게 한다 — 저장한 뒤에 잘못된 걸 발견하면 사진이
     * 이미 어딘가에 들어가 있어 되돌리는 품이 더 든다.
     */
    var pendingVisitPhotos by mutableStateOf<List<PendingVisitPhoto>>(emptyList())
        private set
    var classifyingPhotos by mutableStateOf(false)
        private set

    /**
     * Non-null while the action sheet for one **not-yet-saved** visit photo is open.
     *
     * 저장된 사진은 전체화면 뷰어의 오버플로에서 고친다. 여기 사진은 아직 바이트 상태라
     * 뷰어가 다루지 못하고(파일명이 없다), 그래서 시트만 띄운다 — 다만 탭이 곧 삭제였던
     * 것을 걷어내는 목적은 같다(리뷰어 R-6).
     */
    var pendingPhotoActionIndex by mutableStateOf<Int?>(null)
        private set

    private val placeId = MutableStateFlow<String?>(null)

    val uiState: StateFlow<DetailUiState> = placeId
        .filterNotNull()
        .flatMapLatest { id ->
            combine(observePlace(id), observeVisits(id)) { place, visits ->
                DetailUiState(place = place, visits = visits, loading = false)
            }
        }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5_000),
            initialValue = DetailUiState(loading = true),
        )

    /**
     * Friends who also want to go here (0-37 stage 2), by display name. Empty unless this
     * place is Want to go on my side too; empty offline. Separate from [uiState] so the
     * network never delays the place itself.
     */
    val friendWishNames: StateFlow<List<String>> = combine(uiState, observeFriendWishes()) { state, wishes ->
        val place = state.place ?: return@combine emptyList()
        friendsWhoAlsoWantToGo(place, visitCount = state.visits.size, friendWishes = wishes).map { it.displayName }
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    fun setPlaceId(id: String) { placeId.value = id }

    fun toggleLiked() {
        val id = placeId.value ?: return
        usageTracker.track(UsageEvents.DETAIL_ACTION, mapOf("kind" to "liked"))
        viewModelScope.launch { toggleLikedUseCase(id) }
    }

    fun setVisibility(visibility: Visibility) {
        val id = placeId.value ?: return
        usageTracker.track(UsageEvents.DETAIL_ACTION, mapOf("kind" to "visibility", "to" to visibility.name))
        viewModelScope.launch { setPlaceVisibilityUseCase(id, visibility) }
    }

    fun openLogVisitDialog() {
        usageTracker.track(UsageEvents.DETAIL_ACTION, mapOf("kind" to "log_visit_open"))
        showLogVisitDialog = true
        visitNoteInput = ""
        visitEmotionInput = null
        visitRevisitInput = uiState.value.place?.liked ?: false
    }

    fun onVisitRevisitChange(value: Boolean) { visitRevisitInput = value }

    fun dismissLogVisitDialog() {
        showLogVisitDialog = false
        visitNoteInput = ""
        visitEmotionInput = null
        // 사진도 함께 버린다. 안 지우면 취소하고 다시 열었을 때 지난번에 고른 사진이 남아
        // 있다가 다음 방문에 딸려 들어간다 — 실기기에서 실제로 그렇게 됐다.
        pendingVisitPhotos = emptyList()
        classifyingPhotos = false
    }

    fun onVisitNoteChange(value: String) {
        visitNoteInput = value.take(MAX_VISIT_NOTE_LENGTH)
    }

    /** Single-select, optional: tapping the already-selected emotion clears it. */
    fun onVisitEmotionSelect(emotion: Emotion) {
        visitEmotionInput = if (visitEmotionInput == emotion) null else emotion
    }

    /** picker가 돌려준 바이트를 받아 분류한다. 실패해도 사진은 미분류로 남고 추가는 성공한다. */
    fun onVisitPhotosPicked(images: List<PickedImage>) {
        if (images.isEmpty()) return
        val room = MAX_VISIT_PHOTOS - pendingVisitPhotos.size
        if (room <= 0) return
        val accepted = images.take(room)
        viewModelScope.launch {
            classifyingPhotos = true
            try {
                val classified = accepted.map { picked ->
                    val guess = photoClassifier.classify(picked.bytes)
                    PendingVisitPhoto(bytes = picked.bytes, kind = guess.kind, group = guess.group)
                }
                pendingVisitPhotos = pendingVisitPhotos + classified
            } finally {
                classifyingPhotos = false
            }
        }
    }

    /** 사용자가 목적지를 바꾼다. 종류는 지운다 — 종류가 남아 있으면 그쪽 묶음이 다시 이긴다. */
    fun onVisitPhotoGroupChange(index: Int, group: PhotoGroup) {
        pendingVisitPhotos = pendingVisitPhotos.mapIndexed { i, photo ->
            if (i == index) photo.copy(kind = null, group = group, movedByUser = true) else photo
        }
    }

    fun onVisitPhotoRemove(index: Int) {
        pendingVisitPhotos = pendingVisitPhotos.filterIndexed { i, _ -> i != index }
    }

    fun addVisitNow() {
        val id = placeId.value ?: return
        val note = visitNoteInput.trim().ifBlank { null }
        val emotion = visitEmotionInput?.id
        val photos = pendingVisitPhotos
        val revisit = visitRevisitInput
        viewModelScope.launch {
            try {
                // D3: System.currentTimeMillis() → kotlin.time.Clock (multiplatform)
                val at = Clock.System.now().toEpochMilliseconds()
                if (photos.isEmpty()) {
                    addVisit(id, at, note, emotion)
                } else {
                    addVisitWithPhotos(
                        placeId = id,
                        visitedAt = at,
                        note = note,
                        emotion = emotion,
                        photos = photos.map { VisitPhotoDraft(it.bytes, it.kind, it.group) },
                    )
                }
                if (revisit != (uiState.value.place?.liked ?: false)) toggleLikedUseCase(id)
                usageTracker.track(
                    UsageEvents.DETAIL_ACTION,
                    mapOf(
                        "kind" to "visit_saved",
                        "photos" to photos.size.toString(),
                        "emotion" to (emotion ?: "none"), // fixed enum id, not free text — answers "which feelings go unused" (0-40)
                        "note" to (note != null).toString(),
                    ),
                )
                dismissLogVisitDialog()
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                // 다이얼로그를 닫지 않는다. 적어 둔 메모와 감정과 고른 사진이 그대로 남아야
                // 사진 몇 장을 빼고 다시 시도할 수 있다 — 닫으면 저장도 안 된 채 입력만 사라진다.
                println("DetailViewModel: addVisit failed: ${e.message ?: e::class.simpleName}")
                saveError = when (e) {
                    is ImageStoreFullException ->
                        "Storage is full. Remove a photo and try again."
                    else -> "Couldn't save this visit. Please try again."
                }
            }
        }
    }

    /** 저장이 실패한 이유. 화면이 스낵바로 보여 주고 [dismissSaveError]로 지운다. */
    var saveError: String? by mutableStateOf(null)

    fun dismissSaveError() {
        saveError = null
    }

    fun openPendingPhotoActions(index: Int) {
        if (index in pendingVisitPhotos.indices) pendingPhotoActionIndex = index
    }

    fun dismissPendingPhotoActions() { pendingPhotoActionIndex = null }

    /** Removes the pending visit photo the sheet is open for, and closes it. */
    fun removeTargetedPendingPhoto() {
        val index = pendingPhotoActionIndex ?: return
        onVisitPhotoRemove(index)
        pendingPhotoActionIndex = null
    }

    fun delete(onDeleted: () -> Unit) {
        val id = placeId.value ?: return
        usageTracker.track(UsageEvents.DETAIL_ACTION, mapOf("kind" to "delete"))
        viewModelScope.launch {
            deletePlace(id)
            onDeleted()
        }
    }

    companion object {
        const val MAX_VISIT_NOTE_LENGTH = 120

        /** 방문 한 번에 붙일 수 있는 사진 수. picker의 한 번 고르기 상한과 같은 값이다. */
        const val MAX_VISIT_PHOTOS = 6
    }
}
